import i18n from 'i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/az';
import 'dayjs/locale/ru';
import { initReactI18next } from 'react-i18next';

import az from '@/i18n/locales/az.json';
import ru from '@/i18n/locales/ru.json';

/**
 * Языки интерфейса (ADR-0008). Переводится интерфейс: меню, кнопки, подписи, сообщения.
 * Данные — товары, контрагенты, адреса — остаются как есть (на русском).
 *
 * Словари: src/i18n/locales/ru.json и az.json — одинаковые ключи; полноту проверяет тест.
 */
export const LANGUAGES = ['ru', 'az'] as const;
export type Language = (typeof LANGUAGES)[number];

const STORAGE_KEY = 'sww.language';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/** До входа: последний выбор в этом браузере, иначе язык браузера, иначе русский. */
function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(saved)) return saved;
  } catch {
    // приватный режим — без localStorage
  }
  return navigator.language.toLowerCase().startsWith('az') ? 'az' : 'ru';
}

void i18n.use(initReactI18next).init({
  resources: { ru: { translation: ru }, az: { translation: az } },
  lng: initialLanguage(),
  fallbackLng: 'ru',
  interpolation: { escapeValue: false }, // React сам экранирует
  returnNull: false,
});
dayjs.locale(i18n.language);

/** Сменить язык интерфейса сразу, без перезагрузки. Профиль на сервере обновляет вызывающий. */
export async function changeLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // см. initialLanguage
  }
  dayjs.locale(language);
  document.documentElement.lang = language;
  await i18n.changeLanguage(language);
}

export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : 'ru';
}

export default i18n;
